import Instructors from "@modules/course/instructors";
import CourseContent from "@modules/course/course-content";
import Warranty from "@modules/course/non-access/warranty";
import CourseCategories from "@modules/course/CourseCategories";
import { useAccess } from "./hooks/useAccess";
import { useNonAccess } from "./hooks/useNonAccess";
import { useUI } from "@context/ui-managed-context";
import { useCartContext } from "@context/cart";
import { useRouter } from "next/router";
import { useEffect } from "react";
import cartApi from "@api/cart";
import {
  COURSE_TYPE,
  EXPIRY_STATUS,
  ICartInput,
  PRODUCT_SLUG,
  SoftwarePerquisite,
} from "@types";
import ProductPageLayout from "@modules/course/non-access/layout/PageLayout";
import PrerequestCourses from "@modules/course/access/more-tab/PrerequestCourses";
import PrerequestApps from "@modules/course/access/more-tab/PrerequestApps";
import Headline from "@modules/course/non-access/headline";
import HasCertificateCard from "@modules/course/non-access/content/HasCertificate";
import PendingReleaseSteps from "@modules/course/non-access/content/PendingRelease";
import KeyLearning from "@modules/course/non-access/key-learning";
import { scrollToElementById } from "@utils/ui";
import useProductVideoModal from "@modules/course/non-access/hook/useVideoModal";
import dynamic from "next/dynamic";
import NonAccessSkeleton from "@modules/course/non-access/non-access-skeleton";
import { handleCourseUrl } from "@utils/handleCourseUrl";
import { MODAL_TYPES } from "types/modals";
import { modalActions } from "@context/modals";
import SubscriptionBannerAdvertisement from "@shared/components/subscription-advertisement";

const Details = dynamic(() => import("@modules/course/details"), {
  ssr: false,
});
const NonAccessDescriptionList = dynamic(
  () => import("@modules/course/non-access/DescriptionList"),
  { ssr: false }
);
const StickyBottom = dynamic(
  () => import("@modules/course/non-access/bottom-sticky"),
  { ssr: false }
);
const Preview = dynamic(() => import("@modules/course/non-access/preview"), {
  ssr: false,
});
const CourseComments = dynamic(() => import("@modules/course/CourseComments"), {
  ssr: false,
});
const ProductsSlider = dynamic(
  () => import("@modules/course/non-access/ProductsSlider"),
  { ssr: false }
);

const NonAccess: React.FC = () => {
  const {
    courseData,
    relatedProduct,
    recentProduct,
    recommendedProduct,
    comments,
    isAdmin,
    setFavoriteAction,
  } = useAccess();
  const hasLoaded = Boolean(courseData?.product?.id);
  const {
    product: {
      product_prerequisites,
      prerequisites,
      is_free: isFree,
      title,
      categories,
      id,
      sku,
      attribute_type,
      lessons_count,
      preview,
      course_content,
      description_data: { list },
      hasCertificate,
      klo,
    },
    lessons,
    user: { is_favorite },
  } = courseData || {};
  const {
    headerItems,
    refs,
    isPack,
    isPublished,
    hasPreview,
    showDemo,
    visibleTarget,
    spyOffsetRef,
    courseContentItem,
  } = useNonAccess();
  const { showModalPreview } = useProductVideoModal(true);

  const shouldRenderContent =
    !isPack &&
    isPublished &&
    courseContentItem.filter((d) => d.enable).length >= 2;

  const coursePre = product_prerequisites.filter(
    (f) => f.type === "course"
  ) as ICartInput[];
  const searchCoursePre = prerequisites.filter(
    (f) => (f.type as string) === "search"
  );

  const softWarePreRequests = product_prerequisites.filter(
    (f) => f.type === "software"
  ) as SoftwarePerquisite[];

  const { setStaticToastView } = useUI();
  const { addItem } = useCartContext();
  const { query } = useRouter();
  const { "add-to-cart": addToCart, "coupon-code": couponCode } = query;

  useEffect(() => {
    if (addToCart && addToCart === "product") {
      addItem({ id, sku }).then(() => {
        if (couponCode) {
          cartApi
            .addDiscount(couponCode as string, true)
            .fetch()
            .then((res) => {
              if (!res.status) {
                setStaticToastView("danger", res.message);
              }
            })
            .catch((error) => {
              setStaticToastView(
                "danger",
                error.message || error.error.message || "دوباره تلاش کنید"
              );
            });
        }
      });
    }
  }, [addToCart, couponCode]);

  const productsToShow = relatedProduct?.length
    ? relatedProduct
    : recommendedProduct;

  useEffect(() => {
    document.querySelector("body")?.classList.add("pb-24", "lg:pb-0");
  }, []);

  const renderPreview = Boolean(
    list?.length ||
      (!showDemo && preview?.collections.length) ||
      (showDemo && hasPreview && !isFree)
  );

  useEffect(() => {
    const fullRoute = window.location.href || "";
    const sectionId =
      typeof fullRoute === "string" && fullRoute?.includes?.("#")
        ? (fullRoute.split("#") || []).reverse()?.[0]
        : null;
    if (sectionId) {
      setTimeout(() => {
        const theSection = document.getElementById(sectionId);
        if (theSection) {
          const isMobile = window.innerWidth < 768;
          let offset = 120;
          if (!isMobile) offset += 60;
          if (isAdmin) offset += 50;
          scrollToElementById(sectionId, offset);
        }
      }, 1_000);
    }
  }, []);

  useEffect(() => {
    const actionParam = query?.action;
    const validActions = ["atc", "fav", "abs", "permanent_access_modal"];
    if (
      actionParam &&
      typeof actionParam === "string" &&
      validActions.includes(actionParam)
    ) {
      setTimeout(() => {
        if (actionParam === "atc" && !isFree) {
          addItem({ id: id, sku: sku });
        } else if (actionParam === "fav" && !is_favorite) {
          setFavoriteAction();
        } else if (actionParam === "abs") {
          showModalPreview(lessons?.[0]);
        }
        if (
          actionParam.toLowerCase() === "permanent_access_modal" &&
          courseData.product.details_expire
        ) {
          openPermanentAccessModal();
        }
      }, 1_000);
    }
  }, []);

  const openPermanentAccessModal = () => {
    modalActions.addModal(MODAL_TYPES.COUPON_PRODUCT, {
      config: {
        hideCloseIcon: true,
      },
      data: {
        discount: courseData.product.details_expire?.value_percent || 0,
        expireDay:
          courseData.product.details_expire?.window_end_at_formatted || "",
        price: courseData.product.price || 0,
        product: courseData.product.title || "",
        productUrl: handleCourseUrl(
          courseData.product.attribute_type,
          courseData.product.slug
        ),
        id: String(courseData.product.id),
        expiryStatus:
          courseData.product.details_expire?.expiry_status ||
          EXPIRY_STATUS.EXPIRABLE,
      },
    });
  };

  if (!hasLoaded) return <NonAccessSkeleton />;

  return (
    <>
      <ProductPageLayout
        headerItems={headerItems}
        visibleTarget={visibleTarget}
        spyOffsetRef={spyOffsetRef}
      >
        {renderPreview && (
          <section
            className="max-h-full overflow-hidden h-full relative transition-[max-height] duration-[2s] ease-out px-4 xl:px-0"
            ref={(e) => {
              refs.current[0] = {
                e,
                offset: headerItems.preview.offset,
              };
            }}
            id={headerItems.preview.id}
          >
            {klo?.length ? <KeyLearning keyLearningData={klo} /> : null}
            {Boolean(list?.length) && <NonAccessDescriptionList />}
            {!showDemo && preview?.collections.length ? (
              <Headline
                className="my-8"
                lessonsCount={lessons_count}
                preview={preview}
                courseTitle={title}
                courseContent={course_content}
                id="toc"
              />
            ) : showDemo && hasPreview && !isFree ? (
              <Preview />
            ) : null}
          </section>
        )}
        <div className="px-4 xl:px-0">
          <SubscriptionBannerAdvertisement type="product" isFree={isFree} />
        </div>
        {shouldRenderContent ? (
          <section
            ref={(e) => {
              refs.current[1] = {
                e,
                offset: headerItems.content.offset,
              };
            }}
            id={headerItems.content.id}
            className="px-4 xl:px-0"
          >
            <CourseContent />
          </section>
        ) : null}
        <section
          ref={(e) => {
            refs.current[2] = {
              e,
              offset: headerItems.about.offset,
            };
          }}
          id={headerItems.about.id}
          className="px-4 xl:px-0"
        >
          <Details showTitle={false} />
        </section>
        {!isPublished && <PendingReleaseSteps />}

        {hasCertificate ? (
          <section className="px-4 xl:px-0">
            <HasCertificateCard />
          </section>
        ) : null}
        {Boolean(prerequisites?.length) && (
          <section
            ref={(e) => {
              refs.current[3] = {
                e,
                offset: headerItems.prerequisite.offset,
              };
            }}
            id={headerItems.prerequisite.id}
            className="px-4 xl:px-0"
          >
            <PrerequestCourses
              items={coursePre}
              otherPreRequests={searchCoursePre}
            />
            <PrerequestApps items={softWarePreRequests} />
          </section>
        )}
        {attribute_type !== COURSE_TYPE.SERVICES ? (
          <section
            className="px-4 xl:px-0"
            ref={(e) => {
              refs.current[4] = {
                e,
                offset: headerItems.preview.offset,
              };
            }}
            id={headerItems.aboutInstructor.id}
          >
            <Instructors />
          </section>
        ) : null}
        {Boolean(!isFree && isPublished) && (
          <section className="px-4 xl:px-0" id="warranty">
            <Warranty />
          </section>
        )}
        {Boolean(isPublished && comments?.total) && (
          <section
            ref={(e) => {
              refs.current[6] = {
                e,
                offset: headerItems.comments.offset,
              };
            }}
            id={headerItems.comments.id}
          >
            <CourseComments removePadding />
          </section>
        )}
        {Boolean(categories.length) && (
          <section className="py-6  md:pt-8 md:pb-4   px-4 xl:px-0">
            <CourseCategories removePadding />
          </section>
        )}
      </ProductPageLayout>
      <div className="section-container relative mx-auto w-full px-4 pt-4 md:px-8   xl:px-0">
        <ProductsSlider
          slug={PRODUCT_SLUG.RELATED_PRODUCTS}
          productsToShow={productsToShow || []}
          title="پیشنهادی برای شما"
        />
        <ProductsSlider
          slug={PRODUCT_SLUG.USER_RECENT_VIEW_PRODUCT}
          moreHref="/recently-viewed"
          productsToShow={recentProduct?.products || []}
          title="بازدید‌های اخیر شما"
        />
      </div>

      <StickyBottom />
    </>
  );
};

export default NonAccess;
